/**
 * ForecastControlsFAB - Floating Action Button toolbar for forecast controls
 *
 * A floating toolbar positioned at the bottom of the viewport that provides:
 * - Cumulative/Month-by-Month toggle (always visible)
 * - Filter button with popover containing all filter controls
 * - Active filter count badge
 *
 * Used by RebatesAnalyticsPage and StatusTabView to provide consistent
 * filter/display controls across rebate forecast views.
 */

import React, { useState, useMemo } from 'react';
import { T, useGT } from 'gt-react';
import {
  SlidersHorizontalIcon,
  XIcon,
  EyeIcon,
  GearIcon,
  CaretLeftIcon,
  ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { FilterPanelContent, type FilterGroup } from './FilterPanelContent';
import {
  LineType,
  LINE_TYPE_LABELS,
  isPredictionStrategy,
  getPredictionStrategyColors,
} from '@/types/rebateAnalytics';
// Minimal palette (formerly from the removed MUI md-theme colors)
const colors = {
  success: { main: '#84cc16' },
  black: { main: '#000000' },
  grey: {
    400: '#a8a29e',
    500: '#78716c',
    600: '#57534e',
  } as Record<number, string>,
};

const FORECAST_CHART_LINE_ROW_IDS = {
  estimates: 'estimates',
  reported: 'reported',
  previousYear: 'previousYear',
} as const;

export interface ForecastControlsFABProps {
  /** Whether the chart displays cumulative data */
  isCumulative: boolean;
  /** Handler for cumulative toggle changes */
  onCumulativeChange: (value: boolean) => void;
  /** Filter group configurations */
  filterGroups: FilterGroup[];
  /** Number of active filters */
  activeFilterCount: number;
  /** Handler to clear all filters */
  onClearAll: () => void;
  /** Position of the FAB */
  position?: 'bottom-center' | 'bottom-right';
  /** Additional class names */
  className?: string;
  /** Set of visible line types */
  visibleLines: Set<string>;
  /** Handler for visibility changes */
  onVisibleLinesChange: (lines: Set<string>) => void;
  /** Currently selected primary line */
  primaryLine: string;
  /** Handler for primary line changes */
  onPrimaryLineChange: (line: string) => void;
  /** Whether previous year data is available */
  hasPreviousYearData?: boolean;
  /** Handler to reset all controls to defaults */
  onReset: () => void;
  /** Whether reset is available (if false, button will be disabled) */
  isResetAvailable: boolean;
  /** Currently selected year (used to disable predictions and reported for 2024 and earlier) */
  selectedYear: number | null;
  /** Available prediction strategy keys from data (e.g. ['linear-regression', 'conservative']) */
  availablePredictionStrategies?: string[];
  /** Which prediction algorithm is used for reported values (determines reported future color) */
  reportedAlgorithm?: string;
}

export function ForecastControlsFAB({
  isCumulative,
  onCumulativeChange,
  filterGroups,
  activeFilterCount,
  onClearAll,
  position = 'bottom-center',
  className,
  visibleLines,
  onVisibleLinesChange,
  primaryLine,
  onPrimaryLineChange,
  hasPreviousYearData = false,
  onReset,
  isResetAvailable,
  selectedYear,
  availablePredictionStrategies = [],
  reportedAlgorithm = 'linear-regression',
}: ForecastControlsFABProps) {
  const gt = useGT();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLinesOpen, setIsLinesOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  // Get color for each line type (for non-split lines)
  const getLineColor = (lineType: string): string => {
    if (isPredictionStrategy(lineType)) {
      return getPredictionStrategyColors(lineType).future;
    }
    switch (lineType) {
      case 'estimates':
        return colors.success.main;
      case 'reported':
        return colors.black.main;
      case 'previousYear':
        return colors.grey?.[400] || '#9CA3AF';
      default:
        return '#6B7280';
    }
  };

  // Check if a line type should have split colors
  const hasSplitColors = (lineType: string): boolean => {
    return isPredictionStrategy(lineType) || lineType === 'reported';
  };

  // Get split colors for a line type
  const getSplitColors = (
    lineType: string,
  ): { left: string; right: string } => {
    if (isPredictionStrategy(lineType)) {
      const strategyColors = getPredictionStrategyColors(lineType);
      return { left: strategyColors.past, right: strategyColors.future };
    }
    if (lineType === 'reported') {
      return {
        left: colors.black.main,
        right: getPredictionStrategyColors(reportedAlgorithm).future,
      };
    }
    return { left: '#6B7280', right: '#6B7280' };
  };

  const positionClasses = {
    'bottom-center': 'bottom-6 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-6 right-6',
  };

  // Calculate default visible lines (all available lines)
  const defaultVisibleLines = useMemo(() => {
    const defaultSet = new Set<string>([
      FORECAST_CHART_LINE_ROW_IDS.estimates,
      FORECAST_CHART_LINE_ROW_IDS.reported,
    ]);
    availablePredictionStrategies.forEach((s) => defaultSet.add(s));
    if (hasPreviousYearData) {
      defaultSet.add(FORECAST_CHART_LINE_ROW_IDS.previousYear);
    }
    return defaultSet;
  }, [hasPreviousYearData, availablePredictionStrategies]);

  // Count visible lines that are in the default set
  const visibleLineCount = useMemo(() => {
    let count = 0;
    defaultVisibleLines.forEach((line) => {
      if (visibleLines.has(line)) {
        count++;
      }
    });
    return count;
  }, [visibleLines, defaultVisibleLines]);

  // Check if current state differs from default
  const isNonDefault = useMemo(() => {
    return visibleLineCount !== defaultVisibleLines.size;
  }, [visibleLineCount, defaultVisibleLines.size]);

  return (
    <div
      className={cn(
        'fixed z-[1200] flex items-center gap-3 rounded-full bg-background/95 backdrop-blur-sm border shadow-lg transition-all duration-200',
        isExpanded ? 'px-4 py-2' : 'p-2',
        positionClasses[position],
        className,
      )}
    >
      {isExpanded ? (
        <>
          {/* Cumulative Toggle */}
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-xs whitespace-nowrap',
                !isCumulative
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground',
              )}
            >
              <T context="rebate forecast chart, month-by-month vs cumulative toggle">
                Monthly
              </T>
            </span>
            <Switch
              checked={isCumulative}
              onCheckedChange={onCumulativeChange}
            />
            <span
              className={cn(
                'text-xs whitespace-nowrap',
                isCumulative
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground',
              )}
            >
              <T context="rebate forecast chart, month-by-month vs cumulative toggle">
                Cumulative
              </T>
            </span>
          </div>

          <div className="h-6 w-px shrink-0 border-l border-border" />

          {/* Line Visibility & Primary Selection */}
          <Popover open={isLinesOpen} onOpenChange={setIsLinesOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={isNonDefault ? 'default' : 'ghost'}
                size="sm"
                className={cn(
                  'relative flex items-center gap-2 h-8 px-3',
                  isNonDefault
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'hover:bg-accent',
                )}
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                <span className="text-sm">
                  <T context="rebate forecast FAB lines popover trigger">
                    Lines
                  </T>
                </span>
                {isNonDefault && (
                  <span className="absolute -top-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-purple-900 px-1.5 text-xs font-medium text-white ring-2 ring-background">
                    {visibleLineCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="center"
              className="w-auto min-w-[400px] max-w-[90vw] p-0"
              sideOffset={12}
            >
              <div className="p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">
                        <T context="rebate forecast lines table column">Line</T>
                      </th>
                      <th className="text-center py-2 px-2">
                        <T context="rebate forecast lines table column">
                          Chart
                        </T>
                      </th>
                      <th className="text-center py-2 px-2">
                        <T context="rebate forecast lines table column">
                          Tables
                        </T>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      [
                        { id: FORECAST_CHART_LINE_ROW_IDS.estimates },
                        ...availablePredictionStrategies.map((s) => ({
                          id: s,
                        })),
                        { id: FORECAST_CHART_LINE_ROW_IDS.reported },
                        {
                          id: FORECAST_CHART_LINE_ROW_IDS.previousYear,
                          disabled: !hasPreviousYearData,
                        },
                      ] as Array<{ id: string; disabled?: boolean }>
                    )
                      .filter((line) => {
                        // Hide previousYear completely if unavailable
                        if (
                          line.id ===
                            FORECAST_CHART_LINE_ROW_IDS.previousYear &&
                          line.disabled
                        ) {
                          return false;
                        }
                        return true;
                      })
                      .map((line) => {
                        const isVisible = visibleLines.has(line.id);
                        const isPrimary = primaryLine === line.id;
                        // Disable prediction strategies and reported for 2024 and earlier
                        const isHistoricalYear =
                          selectedYear !== null &&
                          selectedYear <= 2024 &&
                          (isPredictionStrategy(line.id) ||
                            line.id === 'reported');
                        const isDisabled = line.disabled || isHistoricalYear;

                        const handleVisibilityToggle = (checked: boolean) => {
                          const newVisible = new Set(visibleLines);

                          // Prevent unchecking if it's the last visible line
                          if (
                            !checked &&
                            visibleLines.size === 1 &&
                            visibleLines.has(line.id)
                          ) {
                            return; // Don't allow unchecking last line
                          }

                          if (checked) {
                            newVisible.add(line.id);
                          } else {
                            newVisible.delete(line.id);

                            // If unchecking the primary line, auto-switch to first visible line
                            if (line.id === primaryLine) {
                              const remainingVisible = Array.from(newVisible);
                              if (remainingVisible.length > 0) {
                                onPrimaryLineChange(remainingVisible[0]);
                              }
                            }
                          }

                          onVisibleLinesChange(newVisible);
                        };

                        const isSplit = hasSplitColors(line.id);
                        const splitColors = isSplit
                          ? getSplitColors(line.id)
                          : null;

                        return (
                          <tr key={line.id} className="border-b">
                            <td
                              className={cn(
                                'py-2 px-2',
                                !isVisible && !isDisabled ? 'opacity-50' : '',
                              )}
                            >
                              <div className="flex items-center gap-2">
                                {isSplit && splitColors ? (
                                  // Split circle: left half one color, right half another
                                  <div
                                    className="w-3 h-3 rounded-full flex-shrink-0 relative overflow-hidden"
                                    style={{
                                      background: `linear-gradient(to right, ${splitColors.left} 50%, ${splitColors.right} 50%)`,
                                    }}
                                  />
                                ) : (
                                  // Solid circle for non-split lines
                                  <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{
                                      backgroundColor: getLineColor(line.id),
                                    }}
                                  />
                                )}
                                <span
                                  className={cn(
                                    !isVisible && !isDisabled
                                      ? 'text-muted-foreground'
                                      : '',
                                  )}
                                >
                                  {LINE_TYPE_LABELS[line.id as LineType] ||
                                    line.id}
                                </span>
                                {isDisabled && (
                                  <span className="text-xs text-muted-foreground ml-2">
                                    <T context="rebate forecast line unavailable suffix">
                                      (unavailable)
                                    </T>
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-center py-2 px-2">
                              <Checkbox
                                checked={isVisible && !isDisabled}
                                onCheckedChange={handleVisibilityToggle}
                                disabled={isDisabled}
                              />
                            </td>
                            <td className="text-center py-2 px-2">
                              <input
                                type="radio"
                                name="primary-line"
                                checked={isPrimary}
                                disabled={!isVisible || isDisabled}
                                onChange={() => onPrimaryLineChange(line.id)}
                                className="cursor-pointer"
                              />
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <p className="text-xs text-muted-foreground mt-3">
                  <T context="rebate forecast primary line help text">
                    Primary line determines current year table data. Previous
                    year always shows Estimates.
                  </T>
                </p>
              </div>
            </PopoverContent>
          </Popover>

          <div className="h-6 w-px shrink-0 border-l border-border" />

          {/* Filter Button with Popover */}
          <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={activeFilterCount > 0 ? 'default' : 'ghost'}
                size="sm"
                className={cn(
                  'relative flex items-center gap-2 h-8 px-3',
                  activeFilterCount > 0
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'hover:bg-accent',
                )}
              >
                <SlidersHorizontalIcon className="h-4 w-4" />
                <span className="text-sm">
                  <T context="rebate forecast FAB filters popover trigger">
                    Filters
                  </T>
                </span>
                {activeFilterCount > 0 && (
                  <span className="absolute -top-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-purple-900 px-1.5 text-xs font-medium text-white ring-2 ring-background">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="center"
              className="w-auto min-w-[400px] max-w-[90vw] p-0"
              sideOffset={12}
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-sm">
                    <T context="rebate forecast filters panel title">Filters</T>
                  </h3>
                  {activeFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClearAll}
                      className="h-7 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <XIcon className="h-3 w-3 mr-1" />
                      <T context="rebate forecast clear all filters button">
                        Clear all
                      </T>
                    </Button>
                  )}
                </div>

                {/* Filter Controls */}
                <FilterPanelContent filterGroups={filterGroups} />
              </div>
            </PopoverContent>
          </Popover>

          {/* Reset Button - Always visible, disabled when at defaults */}
          <div className="h-6 w-px shrink-0 border-l border-border" />
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={!isResetAvailable}
            className="h-8 w-8 p-0"
            title={
              isResetAvailable
                ? gt('Reset all controls to defaults', {
                    context: 'rebate forecast FAB reset tooltip',
                  })
                : gt('All controls are at default values', {
                    context: 'rebate forecast FAB reset disabled tooltip',
                  })
            }
          >
            <ArrowCounterClockwiseIcon className="h-4 w-4" />
          </Button>

          {/* Collapse Button */}
          <div className="h-6 w-px shrink-0 border-l border-border" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(false)}
            className="h-8 w-8 p-0"
            title={gt('Collapse controls', {
              context: 'rebate forecast FAB collapse toolbar',
            })}
          >
            <CaretLeftIcon className="h-4 w-4" />
          </Button>
        </>
      ) : (
        /* Collapsed State - Gear Icon */
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(true)}
          className="h-8 w-8 p-0"
          title={gt('Expand controls', {
            context: 'rebate forecast FAB expand toolbar',
          })}
        >
          <GearIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default ForecastControlsFAB;
